/**
 * Client-side cookie consent (GDPR-oriented). Persists to localStorage and a first-party cookie
 * so the SPA, *.bravenet.com subdomains, and PHP (`$_COOKIE['bn_consent']`) share one decision.
 *
 * Cookie value: URL-encoded JSON, same shape as localStorage `bn_cookie_consent_v1`:
 *   {"v":1,"necessary":true,"analytics":bool,"marketing":bool,"ts":"ISO8601","choice":"all"|"essential"|"custom"}
 * PHP: `json_decode($_COOKIE['bn_consent'] ?? '', true)` (PHP may URL-decode cookie values automatically).
 */

export const LEGACY_CONSENT_KEY = 'gdpr_opt_in_capturing'
export const CONSENT_KEY = 'bn_cookie_consent_v1'
/** HTTP cookie name — readable by PHP across `Domain=.bravenet.com`. */
export const CONSENT_COOKIE_NAME = 'bn_consent'
export const CONSENT_VERSION = 1

const LEGACY_EPOCH_TS = '1970-01-01T00:00:00.000Z'
const COOKIE_MAX_AGE_SEC = 365 * 24 * 60 * 60

export function consentCookieDomain () {
  if (typeof window === 'undefined') return ''
  const h = window.location.hostname
  if (h === 'localhost' || h.endsWith('.local') || /^\d{1,3}(\.\d{1,3}){3}$/.test(h)) {
    return ''
  }
  if (h === 'bravenet.com' || h.endsWith('.bravenet.com')) {
    return '.bravenet.com'
  }
  return ''
}

function isValidConsentRecord (o) {
  if (!o || o.v !== CONSENT_VERSION) return false
  if (o.necessary != null && o.necessary !== true) return false
  return true
}

export function consentForStorage (o) {
  return {
    v: CONSENT_VERSION,
    necessary: true,
    analytics: !!o.analytics,
    marketing: !!o.marketing,
    ts: o.ts || new Date().toISOString(),
    choice: o.choice || 'custom',
  }
}

function readLocalStorageConsent () {
  try {
    const raw = window.localStorage.getItem(CONSENT_KEY)
    if (!raw) return null
    const o = JSON.parse(raw)
    if (!isValidConsentRecord(o)) return null
    return { ...o, _source: 'localStorage' }
  } catch (e) {
    return null
  }
}

export function readConsentFromCookie () {
  if (typeof document === 'undefined') return null
  try {
    const prefix = `${CONSENT_COOKIE_NAME}=`
    const parts = document.cookie.split(';')
    for (let i = 0; i < parts.length; i++) {
      const p = parts[i].trim()
      if (!p.startsWith(prefix)) continue
      const encoded = p.slice(prefix.length)
      const json = decodeURIComponent(encoded)
      const o = JSON.parse(json)
      if (!isValidConsentRecord(o)) return null
      return { ...o, _source: 'cookie' }
    }
  } catch (e) {
    /* ignore */
  }
  return null
}

function legacySyntheticRecord () {
  try {
    if (window.localStorage.getItem(LEGACY_CONSENT_KEY) === 'yes') {
      return {
        v: CONSENT_VERSION,
        necessary: true,
        analytics: true,
        marketing: true,
        choice: 'all',
        ts: LEGACY_EPOCH_TS,
        _source: 'legacy-yes',
      }
    }
  } catch (e) {
    /* ignore */
  }
  return null
}

function pickNewestConsent (candidates) {
  const rank = { cookie: 3, localStorage: 2, 'legacy-yes': 1 }
  return (
    candidates
      .filter(Boolean)
      .sort((a, b) => {
        const cmp = (b.ts || '').localeCompare(a.ts || '')
        if (cmp !== 0) return cmp
        return (rank[b._source] || 0) - (rank[a._source] || 0)
      })[0] || null
  )
}

/**
 * Sync localStorage + legacy flag + mirror cookie to the winning record so SPA, other tabs, and PHP agree.
 * Call once early in the app (e.g. `created`). Skipped during prerender.
 */
export function reconcileConsentMirrors () {
  if (typeof window === 'undefined' || typeof document === 'undefined') return
  if (/PRERENDER/i.test(window.navigator.userAgent)) return

  const best = pickNewestConsent([
    readLocalStorageConsent(),
    readConsentFromCookie(),
    legacySyntheticRecord(),
  ])
  if (!best) return

  const { _source, ...rest } = best
  const stored = consentForStorage(rest)

  try {
    window.localStorage.setItem(CONSENT_KEY, JSON.stringify(stored))
  } catch (e) {
    /* ignore */
  }
  try {
    if (stored.analytics && stored.marketing) {
      window.localStorage.setItem(LEGACY_CONSENT_KEY, 'yes')
    } else {
      window.localStorage.removeItem(LEGACY_CONSENT_KEY)
    }
  } catch (e) {
    /* ignore */
  }
  writeConsentMirrorCookie(stored)
}

export function writeConsentMirrorCookie (payload) {
  if (typeof document === 'undefined') return
  const compact = consentForStorage(payload)
  try {
    const encoded = encodeURIComponent(JSON.stringify(compact))
    const domain = consentCookieDomain()
    let c = `${CONSENT_COOKIE_NAME}=${encoded};path=/;max-age=${COOKIE_MAX_AGE_SEC};SameSite=Lax`
    if (domain) c += `;domain=${domain}`
    if (typeof window !== 'undefined' && window.location.protocol === 'https:') {
      c += ';Secure'
    }
    document.cookie = c
  } catch (e) {
    /* ignore */
  }
}

export function hasCookieDecision () {
  return readConsent() !== null
}

export function readConsent () {
  const best = pickNewestConsent([
    readLocalStorageConsent(),
    readConsentFromCookie(),
    legacySyntheticRecord(),
  ])
  if (!best) return null
  const { _source, ...rest } = best
  const out = { ...rest }
  if (_source === 'legacy-yes') out.source = 'legacy-yes'
  else if (_source === 'cookie') out.source = 'cookie'
  return out
}

/**
 * Pushes consent to `dataLayer` for GTM (event `bn_cookie_consent`) and, when `gtag` exists,
 * applies Google Consent Mode v2-style updates (analytics_storage / ad_*).
 */
export function notifyConsentTags (consent) {
  if (!consent || consent.v !== CONSENT_VERSION) return
  const analyticsGranted = !!consent.analytics
  const marketingGranted = !!consent.marketing
  const choice = consent.choice || (consent.source === 'legacy-yes' ? 'all' : 'unknown')
  try {
    window.dataLayer = window.dataLayer || []
    window.dataLayer.push({
      event: 'bn_cookie_consent',
      bn_consent: {
        necessary: true,
        analytics: analyticsGranted,
        marketing: marketingGranted,
        choice,
      },
    })
  } catch (e) {
    /* ignore */
  }
  try {
    if (typeof window.gtag === 'function') {
      window.gtag('consent', 'update', {
        analytics_storage: analyticsGranted ? 'granted' : 'denied',
        ad_storage: marketingGranted ? 'granted' : 'denied',
        ad_user_data: marketingGranted ? 'granted' : 'denied',
        ad_personalization: marketingGranted ? 'granted' : 'denied',
      })
    }
  } catch (e) {
    /* ignore */
  }
}

function persist (payload) {
  const compact = consentForStorage(payload)
  try {
    window.localStorage.setItem(CONSENT_KEY, JSON.stringify(compact))
  } catch (e) {
    /* ignore */
  }
  writeConsentMirrorCookie(compact)
}

function afterConsentWrite () {
  notifyConsentTags(readConsent())
}

export function writeConsentAcceptAll () {
  const ts = new Date().toISOString()
  try {
    window.localStorage.setItem(LEGACY_CONSENT_KEY, 'yes')
  } catch (e) {
    /* ignore */
  }
  persist({
    v: CONSENT_VERSION,
    necessary: true,
    analytics: true,
    marketing: true,
    ts,
    choice: 'all',
  })
  afterConsentWrite()
}

export function writeConsentEssentialOnly () {
  const ts = new Date().toISOString()
  try {
    window.localStorage.removeItem(LEGACY_CONSENT_KEY)
  } catch (e) {
    /* ignore */
  }
  persist({
    v: CONSENT_VERSION,
    necessary: true,
    analytics: false,
    marketing: false,
    ts,
    choice: 'essential',
  })
  afterConsentWrite()
}

export function writeConsentCustom ({ analytics, marketing }) {
  const ts = new Date().toISOString()
  try {
    window.localStorage.removeItem(LEGACY_CONSENT_KEY)
  } catch (e) {
    /* ignore */
  }
  persist({
    v: CONSENT_VERSION,
    necessary: true,
    analytics: !!analytics,
    marketing: !!marketing,
    ts,
    choice: 'custom',
  })
  afterConsentWrite()
}

export function applyConsentedClass () {
  try {
    document.documentElement.classList.add('bn-gdpr-consented')
  } catch (e) {
    /* ignore */
  }
}
